import type { User, UserRole, UserRoleProfile } from '@/types';

type AccessProfile = UserRoleProfile;
type AccessUser = Pick<User, 'role' | 'role_profile' | 'department'> | undefined | null;

export const roleLabels: Record<UserRole, string> = {
  admin: '管理员',
  business: '业务员',
  tech: '设计师',
  finance: '财务',
  production: '生产主管',
  client: '客户',
};

export const profileLabels: Record<AccessProfile, string> = {
  admin: '管理员',
  business: '业务员',
  tech: '设计师',
  finance: '财务',
  production_supervisor: '生产主管',
  product_dept: '产品部',
  packaging_dept: '包装部',
  comprehensive_dept: '综合部',
  client: '客户',
};

const defaultHomeMap: Record<UserRole, string> = {
  admin: '/dashboard',
  business: '/business/home',
  tech: '/design',
  finance: '/wages',
  production: '/production',
  client: '/orders',
};

const profilePermissions: Record<AccessProfile, string[]> = {
  admin: ['*'],
  business: ['menu.business', 'menu.design', 'menu.crate_calculator'],
  // menu.materials was merged into menu.inventory on 2026-05-13 — the
  // Materials page (catalog edit + bulk import) is now part of /inventory.
  // The permission token itself is dropped; the inventory page already
  // exposes the same edit/import surface.
  tech: ['menu.dashboard', 'menu.design', 'menu.orders', 'menu.boms'],
  // menu.bom_audits added 2026-05-13: BOM accountant-audit gate. Finance
  // reviews theoretical-vs-actual material consumption per completed order.
  finance: ['menu.dashboard', 'menu.orders', 'menu.inventory', 'menu.wages', 'menu.bom_audits', 'menu.crate_calculator', 'menu.dingtalk_robot'],
  production_supervisor: ['menu.dashboard', 'menu.production', 'menu.inventory'],
  product_dept: ['menu.production'],
  packaging_dept: ['menu.production'],
  comprehensive_dept: ['menu.delivery', 'menu.orders', 'menu.inventory'],
  client: ['menu.client_query'],
};

export const routePermissionMap: Record<string, string | null> = {
  '/': null,
  '/dashboard': 'menu.dashboard',
  '/business': 'menu.business',
  '/admin/commission-rules': 'menu.wages',
  '/admin': 'menu.admin',
  '/orders': 'menu.orders',
  '/design': 'menu.design',
  '/customers': 'menu.customers',
  '/production': 'menu.production',
  '/delivery': 'menu.delivery',
  '/tools/crate-calculator': 'menu.crate_calculator',
  '/inventory': 'menu.inventory',
  '/boms': 'menu.boms',
  '/wages': 'menu.wages',
  '/bom-audits': 'menu.bom_audits',
  '/settings': 'menu.settings',
  '/dingtalk-robot': 'menu.dingtalk_robot',
};

export function getAccessProfile(user: AccessUser): AccessProfile | null {
  if (!user) return null;
  if (user.role_profile) return user.role_profile;
  if (user.role === 'production' && user.department === 'product') return 'product_dept';
  if (user.role === 'production' && user.department === 'packaging') return 'packaging_dept';
  if (user.role === 'production' && user.department === 'comprehensive') return 'comprehensive_dept';
  if (user.role === 'production') return 'production_supervisor';
  return user.role;
}

export function hasPermission(user: AccessUser, permission: string) {
  const dynamicPermissions = Array.isArray((user as any)?.permissions)
    ? ((user as any).permissions as string[])
    : null;
  if (dynamicPermissions) {
    if (permission === 'menu.orders' && dynamicPermissions.includes('menu.client_query')) {
      return true;
    }
    return dynamicPermissions.includes('*') || dynamicPermissions.includes(permission);
  }
  const profile = getAccessProfile(user);
  if (!profile) return false;
  const permissions = profilePermissions[profile] || [];
  if (permission === 'menu.orders' && permissions.includes('menu.client_query')) {
    return true;
  }
  return permissions.includes('*') || permissions.includes(permission);
}

export function getHomePath(user: AccessUser) {
  const profile = getAccessProfile(user);
  const preferredPath = profile === 'comprehensive_dept'
    ? '/delivery'
    : (user?.role ? defaultHomeMap[user.role] : '/dashboard');
  const preferredPermission = getRoutePermission(preferredPath);
  if (!preferredPermission || hasPermission(user, preferredPermission)) {
    return preferredPath;
  }

  const fallbacks = ['/dashboard', '/production', '/orders', '/delivery', '/inventory', '/settings', '/business/home'];
  const fallback = fallbacks.find((path) => {
    const permission = getRoutePermission(path);
    return !permission || hasPermission(user, permission);
  });
  return fallback || '/dashboard';
}

export function getRoleDisplayName(user: AccessUser) {
  const profile = getAccessProfile(user);
  if (profile) return profileLabels[profile];
  return user?.role ? roleLabels[user.role] : '未登录';
}

export function getRoutePermission(pathname: string) {
  const entries = Object.entries(routePermissionMap).sort((a, b) => b[0].length - a[0].length);
  const match = entries.find(([route]) => route !== '/' && (pathname === route || pathname.startsWith(`${route}/`)));
  return match?.[1] || null;
}
